<template>
  <div id="kjk-admin">
    <!-- Main Header -->
    <b-navbar
      id="main-header"
      class="py-1 px-2"
      type="dark"
      variant="primary"
      toggleable="lg"
      sticky>
      <!-- Company & Logo -->
      <b-navbar-brand
        id="logo"
        class="mr-1">
        <img src="./assets/img/KJKRegistrations_White_Small.png" />
      </b-navbar-brand>
      <!-- / Company & Logo -->

      <b-navbar-toggle v-if="loggedInUser.id" target="nav-collapse" />

      <b-collapse
        id="nav-collapse"
        is-nav
        v-if="loggedInUser.id">
        <!-- Main Navigation -->
        <b-navbar-nav
          id="main-navigation">
          <b-nav-text
            class="text-white mx-2"
            v-if="!hasRole('superadmin')">
            {{loggedInUser.actingOrg && loggedInUser.actingOrg.name}}
          </b-nav-text>

          <!-- Organization Drop Down -->
          <b-nav-item-dropdown
            :text="loggedInUser.actingOrg && loggedInUser.actingOrg.name"
            v-if="hasRole('superadmin')">
            <!-- Org Options -->
            <b-dropdown-item
              v-for="o in organizations"
              :key="o.id"
              :disabled="loggedInUser.actingOrg && loggedInUser.actingOrg.id === o.id"
              @click="setActingOrg(o);">
              {{o.name}}
            </b-dropdown-item>
            <!-- / Org Options -->

            <!-- Manage Organizations -->
            <b-dropdown-divider />
            <b-dropdown-item
              @click="$router.push({ name: 'organizations' });">
              Manage Organizations
            </b-dropdown-item>
            <!-- / Manage Organizations -->
          </b-nav-item-dropdown>
          <!-- / Organization Drop Down -->

          <!-- Season Drop Down -->
          <b-nav-item-dropdown
            :text="loggedInUser.actingSeason && loggedInUser.actingSeason.name"
            class="mr-sm-2">
            <!-- Season Options -->
            <b-dropdown-item
              v-for="s in seasons"
              :key="s.id"
              :disabled="loggedInUser.actingSeason && loggedInUser.actingSeason.id === s.id"
              @click="setActingSeason(s);">
              {{s.name}}
            </b-dropdown-item>
            <!-- / Season Options -->

            <b-dropdown-divider v-if="seasons.length" />

            <b-dropdown-item
              :disabled="loggedInUser.actingSeason && !loggedInUser.actingSeason.id"
              @click="setActingSeason({ name: 'All Seasons' });">
              All Seasons
            </b-dropdown-item>

            <!-- Manage Seasons -->
            <b-dropdown-item
              v-if="hasRole('superadmin')"
              @click="$router.push({ name: 'seasons' });">
              Manage Seasons
            </b-dropdown-item>
            <!-- / Manage Seasons -->
          </b-nav-item-dropdown>
          <!-- / Season Drop Down -->

          <!-- Events Link -->
          <b-nav-item
            to="/events"
            :class="{
              active: $route.path.includes('events')
            }">
            <font-awesome-icon icon="calendar-alt"></font-awesome-icon> Events
          </b-nav-item>
          <!--/  Events Link -->

          <!-- Registrants Link -->
          <b-nav-item
            to="/registrants"
            :class="{
              active: $route.path.includes('registrants') && !$route.path.includes('events')
            }">
            <font-awesome-icon icon="users"></font-awesome-icon> Registrants
          </b-nav-item>
          <!-- / Registrants Link -->

          <!-- Reports Link -->
          <b-nav-item
            to="/reports"
            :class="{
              active: $route.path.includes('reports')
            }">
            <font-awesome-icon icon="copy"></font-awesome-icon> Reports
          </b-nav-item>
          <!-- / Reports Link -->

          <!-- Communication Link -->
          <b-nav-item
            to="/communications"
            :class="{
              active: $route.path.includes('communications')
            }">
            <font-awesome-icon icon="envelope"></font-awesome-icon> Messaging
          </b-nav-item>
          <!-- / Communication Link -->

          <!-- Accounting -->
          <b-nav-item
            to="/cc-transactions"
            :class="{
              active: $route.path.includes('cc-transactions')
            }">
            <font-awesome-icon icon="credit-card"></font-awesome-icon> Transactions
          </b-nav-item>
          <!-- / Accounting -->

          <!-- Users -->
          <b-nav-item
            v-if="hasRole('superadmin')"
            to="/organizers"
            :class="{
              active: $route.path.includes('organizers')
            }">
            <font-awesome-icon icon="users-cog"></font-awesome-icon> Organizers
          </b-nav-item>
          <!-- / Users -->
        </b-navbar-nav>
        <!-- / Main Navigation -->

        <!-- User Menu -->
        <b-navbar-nav
          class="ml-auto">
          <b-nav-item-dropdown right>
            <template slot="button-content">
              <font-awesome-icon icon="user-circle"></font-awesome-icon> {{loggedInUser.username}}
            </template>
            <b-dropdown-item
              href="#"
              v-on:click="signOut()">
              Sign Out
            </b-dropdown-item>
          </b-nav-item-dropdown>
        </b-navbar-nav>
        <!-- / User Menu -->
      </b-collapse>
    </b-navbar>
    <!-- / Main Header -->

    <!-- Main Content -->
    <div id="main-content">
      <router-view
        v-if="enableRouting"
        class="page-content"
      />
    </div>
    <!-- / Main Content -->

    <!-- Global Loading Indicator -->
    <GlobalLoader variant="light" />
    <!-- / Global Loading Indicator -->

    <!-- Global Toast -->
    <GlobalToast />
    <!-- / Global Toast -->

    <!-- Modals -->
    <!-- Confirm Dialog -->
    <ConfirmDialog />
    <!-- / Confirm Dialog -->

    <!-- Warning Dialog -->
    <WarningDialog />
    <!-- / Warning Dialog -->
    <!-- / Modals -->
  </div>
</template>

<script>
import _ from 'lodash';
import Session from './services/user-session';
import DataSvc from './services/data';
import DirtyFormMixin from './services/dirty-form';
import EventBus from './services/event-bus';
import AppMixin from './services/app-mixin';
import BrowserStorageMixin from './services/browser-storage-mixin';

const noAuthRoutes = [
  '/signin',
];

export default {
  name: 'App',
  mixins: [
    DirtyFormMixin,
    AppMixin,
    BrowserStorageMixin,
  ],
  computed: {
    // enable routing if there is a logged in user or if the route is signin
    enableRouting() {
      return this.loggedInUser.id || noAuthRoutes.includes(this.$route.path);
    },
  },
  methods: {
    signOut() {
      const token = this.loggedInUser.auth_token;
      this.$router.replace({ name: 'signin' });
      this.loggedInUser = {};
      return DataSvc.signOut(token).then(() => {
        Session.end();
        this.sessionStorageDelete('referrer');
      });
    },
    setActingOrg(organization) {
      this.loggedInUser.actingOrg = _.cloneDeep(organization);
      this.setActingSeason({ name: 'All Seasons' });
      EventBus.$emit('acting-org-changed', organization.id);
      this.getSeasons(organization.id);
    },
    setActingSeason(season) {
      this.loggedInUser.actingSeason = _.cloneDeep(season);
      Session.update(this.loggedInUser);
      EventBus.$emit('acting-season-changed', season.id);
      this.$forceUpdate();
    },
    getSeasons(organizationID) {
      return DataSvc.getSeasons(organizationID)
        .then((seasons) => {
          this.seasons = seasons;
        });
    },
  },
  watch: {
    $route(newRoute) {
      // ensure there's always a user present otherwise force a sign in
      if (!this.loggedInUser.id && !noAuthRoutes.includes(newRoute.path)) {
        this.$router.replace({ name: 'signin' });
      }
    },
  },
  created() {
    // handle API errors
    DataSvc.configureErrorHandling((response) => {
      // form bulleted list of errors
      let errList = _.map(
        response.data.errors,
        e => `&bull; ${e.message}`,
      ).join('<br>');

      // if unauthorized response on any non-sign in request
      if (response.status === 401
          && !response.config.url.includes('users/auth_token')) {
        errList = '&bull; Your session has expired. Please sign in again.';
        this.$router.replace({ name: 'signin' });
        this.loggedInUser = {};
        Session.end();
      }

      this.showWarning(errList, 'The following errors occured');
    });

    // initialize session
    const sessionPromise = Session.resume();

    this.initPromise = sessionPromise
      .then((data) => {
        [this.loggedInUser, this.organizations] = data;

        EventBus.$emit('logged-in-user-changed');

        // if acting org not already set
        // set acting org as first returned org
        if (!this.loggedInUser.actingOrg) {
          [this.loggedInUser.actingOrg] = _.cloneDeep(this.organizations);
        }

        if (!this.loggedInUser.actingSeason) {
          this.loggedInUser.actingSeason = { name: 'All Seasons' };
        }

        Session.update(this.loggedInUser);

        return this.getSeasons(this.loggedInUser.actingOrg.id);
      }, () => {
        this.$router.replace({ name: 'signin' });
      });

    // acting org / season listeners
    EventBus.$on('set-acting-org', this.setActingOrg);
    EventBus.$on('set-acting-season', this.setActingSeason);

    // handle warnings
    EventBus.$on('show-warning', (warning) => {
      this.showWarning(warning.msg, warning.title);
    });
  },
};
</script>

<style lang="scss">
#logo {
  img {
    height: 28px;
  }
}

#main-header {
  min-height: 49px;

  .b-nav-dropdown {
    a.dropdown-toggle {
      color: #ffffff;
    }
  }

  #main-navigation {
    .nav-item {
      .nav-link {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      &:not(.enforce-exact) {
        .router-link-active {
          color: #FFC645;
        }
      }

      &.enforce-exact {
        .router-link-exact-active {
          color: #FFC645;
        }
      }
    }
  }
}
</style>
